import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ConfigProvider, Layout, Result, Typography } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { antdTheme } from './theme/antd';
import { InstitutionProvider } from './contexts/InstitutionContext';
import { ProductShell, ProductSurface } from './layout/ProductShell';
import { AdminShell } from './layout/admin/AdminShell';

// Setup screens
import { SetupPage } from './features/setup/SetupPage';
import { ImportPage } from './features/setup/steps/ImportPage';
import { CalendarSetupPage } from './features/setup/steps/CalendarSetupPage';
import { StaffSetupPage } from './features/setup/steps/StaffSetupPage';
import { FeesSetupPage } from './features/setup/steps/FeesSetupPage';
import { GuardiansSetupPage } from './features/setup/steps/GuardiansSetupPage';
import { AttendanceModePage } from './features/setup/steps/AttendanceModePage';
import { StructurePage } from './features/admin/structure/StructurePage';
import {
  YearsPage, SubjectsPage, ProgramsPage, RoomsPage,
} from './features/admin/structure/StructureExtrasPages';
import { ConcessionsPage } from './features/admin/fees/ConcessionsPage';
import { BonafidePage } from './features/admin/records/BonafidePage';
import { HomeworkPage } from './features/admin/homework/HomeworkPage';
import { CompliancePage } from './features/admin/compliance/CompliancePage';
import { GuardiansListPage } from './features/admin/guardians/GuardiansListPage';
import { GuardianDetailPage } from './features/admin/guardians/GuardianDetailPage';
import { StaffDetailPage } from './features/admin/staff/StaffDetailPage';
// Student surface
import { StudentShell } from './features/student/StudentShell';
import {
  StudentHomePage, StudentTimetablePage, StudentMarksPage, StudentAttendancePage, StudentFeesPage,
  StudentDocumentsPage, StudentMessagesPage, StudentAssignmentDetailPage,
} from './features/student/StudentPages';
import { StudentHomeworkPage } from './features/student/StudentHomeworkPage';

// Admin daily loop
import { BriefingPage } from './features/admin/briefing/BriefingPage';
import { StudentsListPage } from './features/admin/students/StudentsListPage';
import { StudentProfilePage } from './features/admin/students/StudentProfilePage';
import { InboxPage } from './features/admin/inbox/InboxPage';
import { InquiriesPage } from './features/admin/admissions/InquiriesPage';
import { ApplicationVerificationPage } from './features/admin/admissions/ApplicationVerificationPage';
import {
  MeritPage, AllocationPage, AdmissionSettingsPage,
} from './features/admin/admissions/AdmissionsExtrasPages';
import { StaffListPage } from './features/admin/staff/StaffListPage';
import { AttendanceTodayPage } from './features/admin/attendance/AttendanceTodayPage';
import { SubstitutionsPage } from './features/admin/timetable/SubstitutionsPage';
import { TimetableBuilderPage } from './features/admin/timetable/TimetableBuilderPage';
import { CalendarRuntimePage } from './features/admin/calendar/CalendarRuntimePage';
import { CollectionsPage } from './features/admin/fees/CollectionsPage';
import { DefaultersPage } from './features/admin/fees/DefaultersPage';
import { FeeStructuresEditorPage } from './features/admin/fees/FeeStructuresEditorPage';
import { BroadcastComposerPage } from './features/admin/comms/BroadcastComposerPage';
import { RolesPage } from './features/admin/settings/RolesPage';
import { RolloverPage } from './features/admin/settings/RolloverPage';
import { SettingsPeoplePage } from './features/admin/settings/SettingsPeoplePage';
import { CustomFieldsPage } from './features/admin/settings/CustomFieldsPage';

// Assessment
import { ExamSetupPage } from './features/admin/exams/ExamSetupPage';
import { MarksProgressPage } from './features/admin/exams/MarksProgressPage';
import { ResultsModerationPage } from './features/admin/exams/ResultsModerationPage';
import { ReportCardBuilderPage } from './features/admin/exams/ReportCardBuilderPage';

// Records
import { TransferCertificatePage } from './features/admin/records/TransferCertificatePage';
import { AuditPage } from './features/admin/records/AuditPage';

// Money out & estate
import { RequisitionsPage } from './features/admin/expenses/RequisitionsPage';
import { QuoteComparisonPage } from './features/admin/expenses/QuoteComparisonPage';
import { BillsPage } from './features/admin/expenses/BillsPage';
import { PayrollPage } from './features/admin/hr/PayrollPage';
import { LeavePage } from './features/admin/hr/LeavePage';
import { MessagesPage } from './features/admin/messaging/MessagesPage';
import { MaintenancePage } from './features/admin/estate/MaintenancePage';
import { TransportPage } from './features/admin/estate/TransportPage';
import {
  AssetsRegisterPage, AssetDetailPage, InventoryPage, RecruitmentPage, HrAttendancePage,
} from './features/admin/estate/EstateHrPages';
import { TransportAssignPage } from './features/admin/estate/TransportAssignPage';
import {
  BroadcastDetailPage, ConsentsPage, ConsentDetailPage, CertVerifierPage,
} from './features/admin/comms/CommsDetailPages';
import { KioskShell } from './features/kiosk/KioskShell';
import {
  KioskIdlePage, KioskScanPage, KioskLookupPage, KioskVehiclesPage, KioskShiftPage,
} from './features/kiosk/KioskPages';

// Org console
import { OrgDashboardPage } from './features/org/OrgDashboardPage';
import { OrgInstitutionsPage } from './features/org/OrgInstitutionsPage';
import { OrgFinancePage } from './features/org/OrgFinancePage';
import { OrgTransfersPage } from './features/org/OrgTransfersPage';
import { OrgAddInstitutionPage } from './features/org/OrgAddInstitutionPage';

// Teacher + Guardian surfaces
import { TeacherShell } from './features/teacher/TeacherShell';
import { TeacherTodayPage } from './features/teacher/TeacherTodayPage';
import { TeacherProfilePage } from './features/teacher/TeacherProfilePage';
import { TeacherLeavesPage } from './features/teacher/TeacherLeavesPage';
import { TeacherMessagesPage } from './features/teacher/TeacherMessagesPage';
import { TeacherWeekPage } from './features/teacher/TeacherWeekPage';
import { TeacherRosterPage } from './features/teacher/TeacherRosterPage';
import { TeacherPayslipsPage } from './features/teacher/TeacherPayslipsPage';
import { TeacherRequestsPage } from './features/teacher/TeacherRequestsPage';
import {
  TeacherGradebookPage, TeacherDiaryNewPage, TeacherRemarksPage,
} from './features/teacher/TeacherAdvancedPages';
import { TeacherAttendancePage } from './features/teacher/TeacherAttendancePage';
import { TeacherOfflineQueuePage } from './features/teacher/TeacherOfflineQueuePage';
import { GuardianShell } from './features/guardian/GuardianShell';
import { GuardianHomePage } from './features/guardian/GuardianHomePage';
import { GuardianWardPage } from './features/guardian/GuardianWardPage';
import { GuardianMessagesPage, GuardianMessageThreadPage } from './features/guardian/GuardianMessagesPage';
import {
  GuardianTimetablePage,
  GuardianResultsIndexPage,
  GuardianExamDetailPage,
  GuardianDocumentsPage,
  GuardianAbsencePage,
  GuardianFeesHistoryPage,
  GuardianYearPage,
  GuardianProfilePage,
  GuardianTransportPage,
  GuardianConsentsPage,
} from './features/guardian/GuardianPages';

// Accounts desk + Front desk surfaces
import { AccountsDeskShell } from './features/accounts_desk/AccountsDeskShell';
import { AccountsCounterPage } from './features/accounts_desk/AccountsCounterPage';
import { AccountsReceiptsPage } from './features/accounts_desk/AccountsReceiptsPage';
import {
  AccountsPettyCashPage, AccountsReconciliationPage, AccountsReportsPage,
} from './features/accounts_desk/AccountsPages';
import { RefundsPage } from './features/accounts_desk/RefundsPage';
import { FrontDeskShell } from './features/front_desk/FrontDeskShell';
import { VisitorsPage } from './features/front_desk/VisitorsPage';
import { GatePassesPage } from './features/front_desk/GatePassesPage';
import { WalkinInquiryPage, CallLogPage } from './features/front_desk/FrontDeskPages';

import { Placeholder } from './pages/Placeholder';

// Feature nav auto-discovery (NN-16). Sidebar reads what modules register.
import.meta.glob('./features/admin/nav.tsx', { eager: true });

const qc = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

function Landing() {
  return (
    <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
      <div style={{ maxWidth: 640 }}>
        <Result
          status="info"
          title="Velora"
          subTitle={
            <Typography.Text type="secondary">
              This is the product host. Open <code>/i/&lt;institution&gt;/admin</code> or{' '}
              <code>/i/&lt;institution&gt;/setup</code> for a specific school. Provision one from the
              Platform Console at <code>http://127.0.0.1:5174</code>.
            </Typography.Text>
          }
        />
      </div>
    </Layout>
  );
}

export function App() {
  return (
    <ConfigProvider theme={antdTheme}>
      <QueryClientProvider client={qc}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />

            <Route
              path="/i/:institution"
              element={
                <InstitutionProvider>
                  <ProductShell />
                </InstitutionProvider>
              }
            >
              {/* Admin surface with sidebar — brings its own padded container. */}
              <Route path="admin" element={<AdminShell />}>
                <Route index element={<BriefingPage />} />
                <Route path="inbox" element={<InboxPage />} />

                {/* Admissions */}
                <Route path="admissions/inquiries" element={<InquiriesPage />} />
                <Route path="admissions/applications" element={<ApplicationVerificationPage />} />
                <Route path="admissions/applications/:code" element={<ApplicationVerificationPage />} />
                <Route path="admissions/merit" element={<MeritPage />} />
                <Route path="admissions/allocation" element={<AllocationPage />} />
                <Route path="admissions/settings" element={<AdmissionSettingsPage />} />

                {/* People */}
                <Route path="students" element={<StudentsListPage />} />
                <Route path="students/:code" element={<StudentProfilePage />} />
                <Route path="guardians" element={<GuardiansListPage />} />
                <Route path="guardians/:code" element={<GuardianDetailPage />} />
                <Route path="staff" element={<StaffListPage />} />
                <Route path="staff/:code" element={<StaffDetailPage />} />

                {/* Academics */}
                <Route path="attendance" element={<AttendanceTodayPage />} />
                <Route path="timetable" element={<TimetableBuilderPage />} />
                <Route path="timetable/substitutions" element={<SubstitutionsPage />} />
                <Route path="timetable/builder" element={<TimetableBuilderPage />} />
                <Route path="exams" element={<ExamSetupPage />} />
                <Route path="exams/:code" element={<ExamSetupPage />} />
                <Route path="exams/:code/marks" element={<MarksProgressPage />} />
                <Route path="exams/:code/results" element={<ResultsModerationPage />} />
                <Route path="reportcards/:code" element={<ReportCardBuilderPage />} />
                <Route path="structure/calendar" element={<CalendarRuntimePage />} />

                {/* Finance */}
                <Route path="fees/collections" element={<CollectionsPage />} />
                <Route path="fees/defaulters" element={<DefaultersPage />} />
                <Route path="fees/structures" element={<FeeStructuresEditorPage />} />
                <Route path="fees/concessions" element={<ConcessionsPage />} />
                <Route path="expenses/requisitions" element={<RequisitionsPage />} />
                <Route path="expenses/quotes/:code" element={<QuoteComparisonPage />} />
                <Route path="expenses/bills" element={<BillsPage />} />
                <Route path="hr/payroll" element={<PayrollPage />} />
                <Route path="hr/payroll/:month" element={<PayrollPage />} />
                <Route path="hr/leave" element={<LeavePage />} />
                <Route path="maintenance" element={<MaintenancePage />} />
                <Route path="transport/routes" element={<TransportPage />} />
                <Route path="transport/assignments" element={<TransportAssignPage />} />
                <Route path="assets" element={<AssetsRegisterPage />} />
                <Route path="assets/:code" element={<AssetDetailPage />} />
                <Route path="inventory" element={<InventoryPage />} />
                <Route path="hr/attendance" element={<HrAttendancePage />} />
                <Route path="hr/recruitment" element={<RecruitmentPage />} />

                {/* Records */}
                <Route path="comms/broadcasts" element={<BroadcastComposerPage />} />
                <Route path="comms/broadcasts/:code" element={<BroadcastDetailPage />} />
                <Route path="comms/messages" element={<MessagesPage />} />
                <Route path="comms/consents" element={<ConsentsPage />} />
                <Route path="comms/consents/:code" element={<ConsentDetailPage />} />
                <Route path="certificates/tc" element={<TransferCertificatePage />} />
                <Route path="certificates/bonafide" element={<BonafidePage />} />
                <Route path="certificates/verify" element={<CertVerifierPage />} />
                <Route path="settings/audit" element={<AuditPage />} />
                <Route path="compliance" element={<CompliancePage />} />

                {/* Homework */}
                <Route path="homework" element={<HomeworkPage />} />
                <Route path="homework/:code" element={<HomeworkPage />} />

                {/* Settings */}
                <Route path="structure/grades" element={<StructurePage />} />
                <Route path="structure/years" element={<YearsPage />} />
                <Route path="structure/subjects" element={<SubjectsPage />} />
                <Route path="structure/programs" element={<ProgramsPage />} />
                <Route path="structure/rooms" element={<RoomsPage />} />
                <Route path="settings/roles" element={<RolesPage />} />
                <Route path="settings/people" element={<SettingsPeoplePage />} />
                <Route path="settings/fields" element={<CustomFieldsPage />} />
                <Route path="promotions" element={<RolloverPage />} />
              </Route>

              {/* Non-Admin product surfaces share a padded container. */}
              <Route element={<ProductSurface />}>
                {/* Setup landing + steps */}
                <Route path="setup" element={<SetupPage />} />
                <Route path="setup/students" element={<ImportPage />} />
                <Route path="setup/calendar" element={<CalendarSetupPage />} />
                <Route path="setup/staff" element={<StaffSetupPage />} />
                <Route path="setup/fees" element={<FeesSetupPage />} />
                <Route path="setup/attendance-mode" element={<AttendanceModePage />} />
                <Route path="setup/invite-guardians" element={<GuardiansSetupPage />} />

                {/* Group / Org console */}
                <Route path="o" element={<OrgDashboardPage />} />
                <Route path="o/institutions" element={<OrgInstitutionsPage />} />
                <Route path="o/institutions/new" element={<OrgAddInstitutionPage />} />
                <Route path="o/finance/collections" element={<OrgFinancePage />} />
                <Route path="o/staff" element={<OrgTransfersPage />} />

                {/* Teacher surface — mobile-first */}
                <Route path="teacher" element={<TeacherShell />}>
                  <Route index element={<TeacherTodayPage />} />
                  <Route path="messages" element={<TeacherMessagesPage />} />
                  <Route path="leaves" element={<TeacherLeavesPage />} />
                  <Route path="profile" element={<TeacherProfilePage />} />
                  <Route path="timetable" element={<TeacherWeekPage />} />
                  <Route path="class/:grade/:section" element={<TeacherRosterPage />} />
                  <Route path="payslips" element={<TeacherPayslipsPage />} />
                  <Route path="requests" element={<TeacherRequestsPage />} />
                  <Route path="gradebook" element={<TeacherGradebookPage />} />
                  <Route path="diary/new" element={<TeacherDiaryNewPage />} />
                  <Route path="remarks/:exam" element={<TeacherRemarksPage />} />
                  <Route path="attendance/:grade/:section" element={<TeacherAttendancePage />} />
                  <Route path="offline" element={<TeacherOfflineQueuePage />} />
                </Route>

                {/* Guardian surface — one shell, ward pages nested */}
                <Route path="parent" element={<GuardianShell />}>
                  <Route index element={<GuardianHomePage />} />
                  <Route path="messages" element={<GuardianMessagesPage />} />
                  <Route path="messages/:thread" element={<GuardianMessageThreadPage />} />
                  <Route path="profile" element={<GuardianProfilePage />} />
                  <Route path=":student" element={<GuardianWardPage />} />
                  <Route path=":student/timetable" element={<GuardianTimetablePage />} />
                  <Route path=":student/results" element={<GuardianResultsIndexPage />} />
                  <Route path=":student/results/:examId" element={<GuardianExamDetailPage />} />
                  <Route path=":student/documents" element={<GuardianDocumentsPage />} />
                  <Route path=":student/absence/new" element={<GuardianAbsencePage />} />
                  <Route path=":student/fees/history" element={<GuardianFeesHistoryPage />} />
                  <Route path=":student/year" element={<GuardianYearPage />} />
                  <Route path=":student/transport" element={<GuardianTransportPage />} />
                  <Route path=":student/consents" element={<GuardianConsentsPage />} />
                </Route>
                {/* Student surface — self-service */}
                <Route path="student" element={<StudentShell />}>
                  <Route index element={<StudentHomePage />} />
                  <Route path="timetable" element={<StudentTimetablePage />} />
                  <Route path="homework" element={<StudentHomeworkPage />} />
                  <Route path="homework/:code" element={<StudentAssignmentDetailPage />} />
                  <Route path="marks" element={<StudentMarksPage />} />
                  <Route path="attendance" element={<StudentAttendancePage />} />
                  <Route path="fees" element={<StudentFeesPage />} />
                  <Route path="documents" element={<StudentDocumentsPage />} />
                  <Route path="messages" element={<StudentMessagesPage />} />
                </Route>

                {/* Accounts desk — the fee counter */}
                <Route path="accounts" element={<AccountsDeskShell />}>
                  <Route index element={<AccountsCounterPage />} />
                  <Route path="receipts" element={<AccountsReceiptsPage />} />
                  <Route path="defaulters" element={<DefaultersPage />} />
                  <Route path="bills" element={<BillsPage />} />
                  <Route path="petty-cash" element={<AccountsPettyCashPage />} />
                  <Route path="reconciliation" element={<AccountsReconciliationPage />} />
                  <Route path="reports" element={<AccountsReportsPage />} />
                  <Route path="refunds" element={<RefundsPage />} />
                </Route>

                {/* Front desk / gate kiosk */}
                <Route path="desk" element={<FrontDeskShell />}>
                  <Route index element={<VisitorsPage />} />
                  <Route path="gate-passes" element={<GatePassesPage />} />
                  <Route path="inquiries/new" element={<WalkinInquiryPage />} />
                  <Route path="calls" element={<CallLogPage />} />
                </Route>

                {/* Gate kiosk — camera-first mobile surface */}
                <Route path="gate" element={<KioskShell />}>
                  <Route index element={<KioskIdlePage />} />
                  <Route path="scan" element={<KioskScanPage />} />
                  <Route path="lookup" element={<KioskLookupPage />} />
                  <Route path="vehicles" element={<KioskVehiclesPage />} />
                  <Route path="shift" element={<KioskShiftPage />} />
                </Route>
              </Route>

              <Route index element={<Navigate to="admin" replace />} />
              <Route path="*" element={<Navigate to="admin" replace />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ConfigProvider>
  );
}
